import * as React from 'react';

import { useGetUserQuery } from 'lib/api/users/getUser';
import {
  useGetWhiteLabelQuery,
  IWhiteLabel,
} from 'lib/api/whitelabel/getWhiteLabel';
import { LoadingIndicator } from 'lib/components';
import { AccessRole } from 'lib/const';
import { toString } from 'lib/utils/functions';
import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { usersKeys } from 'lib/api/users/queryKeys';
import { notificationKeys } from 'lib/api/notifications/queryKeys';
import { getSuperAdminRole } from 'lib/utils/superAdminRole';
import {
  ILocation,
  useGetLocationsQuery,
} from 'lib/api/locations/getUserLocations';
import { isRegionsReseller } from 'lib/utils/productFeature';
import { getAutomotiveFlags } from 'lib/utils/automotiveRolePermissionChecks';
import { useGetOrganizationAccessQuery } from 'lib/api/organizations/getOrganizationAccess';
import { getCurrentToken } from 'configs/api/token';
import { getThemeByWhiteLabel } from '../theme/utils';
import * as Sentry from '@sentry/browser';
import { useToastError } from 'lib/hooks';
import { updateAxiosInstaceToken } from 'configs/api/helpers';
import { useTranslation } from 'react-i18next';
import {
  ICombinedUserData,
  IDecodedToken,
  IUserRoles,
  LANGAUGES,
  THEME_NAME,
} from './types';
import { calculateTrialDaysLeft, decodeJwt } from './utils';
import { useGTMInject } from 'lib/hooks/GTM/useGTMInject';
import { useLogoutUser } from 'lib/api/users/useLogoutUser';

interface IAuthState {
  userData: ICombinedUserData;
  whitelabel: IWhiteLabel;
  setJwtAndUpdateToken: (
    tokens: {
      accessToken: string;
      refreshToken?: string;
      adminJwt?: string;
    },
    shouldStoreTokens?: boolean
  ) => void;
  invalidateUser: () => Promise<void>;
  invalidateNotificationCount: () => Promise<void>;
  themeName: THEME_NAME;
  locations: ILocation[];
}

const authState = {} as IAuthState;

const AuthContext = React.createContext(authState);
/*  STEPS TO LOAD THE APP
1. Call whitelabel API, generate links and set usebeta cookie
2. After the whitelabel call is finished we are calling get user.
On get user ERROR we are checking if status is 401 and reddirecting user to logut
or we throw an error if user is undefined or api throws an error(404, 500, etc...).

On get user SUCCESS we are setting theme and setting favicon
*/

function getInitialJwt(): string | undefined {
  const urlParams = new URLSearchParams(window.location.search);
  const urlJWT = urlParams.get('token');

  if (urlJWT) {
    updateAxiosInstaceToken(urlJWT);
    urlParams.delete('token');
    window.history.replaceState(null, '', `${window.location.pathname}`);
    return urlJWT;
  }

  const { token } = getCurrentToken();
  return token;
}

function AuthProvider(props: any) {
  const [jwt, setJwt] = useState<string | undefined>(undefined);
  const [themeName, setThemeName] = useState<THEME_NAME>(THEME_NAME.COVIDEO);
  const { showError } = useToastError();
  const { i18n } = useTranslation();

  const queryClient = useQueryClient();
  const isSignup = window.location.pathname.includes('signup');
  const isUnsubscribe = window.location.pathname.includes('unsubscribe');
  const isResetPassword = window.location.pathname.includes(
    'verify-reset-password'
  );
  const { isLoadingScript } = useGTMInject(themeName === THEME_NAME.CDK);

  //FIRST STEP: GET WHITE_LABEL
  const { data: whitelabel, isLoading: isLoadingWhiteLabel } =
    useGetWhiteLabelQuery();
  //SECOND STEP: GET USER DATA
  const { data, isLoading, isError } = useGetUserQuery(whitelabel);
  const { mutateAsync: logoutUser } = useLogoutUser();

  const hasLocationsEnabled = !!(
    data?.customer?.hasMultiLocation?.toString() === '1'
  );

  // GET LOCATIONS DATA
  const { data: locations, isLoading: isLoadingLocations } =
    useGetLocationsQuery(hasLocationsEnabled, data?.id || 0);

  // GET ORGANIZATION ACCESS DATA
  const {
    data: userOrganizationAccess = [],
    isLoading: isLoadingOrganizationAccess,
  } = useGetOrganizationAccessQuery({ enabled: !!whitelabel && !!data });

  useEffect(() => {
    if (jwt) {
      return;
    }
    const token = getInitialJwt();
    setJwt(token);
  }, [jwt]);

  // set the language for i18n based on user preference
  useEffect(() => {
    i18n.changeLanguage(data?.language || LANGAUGES.EN);
  }, [data?.language]);

  useEffect(() => {
    if (!whitelabel || (!data && !isUnsubscribe)) {
      return;
    }
    setThemeName(getThemeByWhiteLabel(whitelabel, data));
  }, [whitelabel, data, isUnsubscribe]);

  // if user error or loading show loading indicator and wait onError in userQuery
  if (
    isError ||
    isLoading ||
    isLoadingWhiteLabel ||
    isLoadingLocations ||
    isLoadingOrganizationAccess ||
    isLoadingScript
  ) {
    return (
      <LoadingIndicator
        text='Loading...'
        isLoading={true}
        extendStyles={{
          h1: {
            fontFamily: 'sans-serif',
          },
        }}
      />
    );
  }

  if (!(isSignup || isResetPassword || isUnsubscribe) && !data) {
    showError('Something went wrong with loading user!');
  }

  if (!(isSignup || isResetPassword || isUnsubscribe) && !jwt) {
    const redirectParam =
      whitelabel?.name === 'Covideo' ? `${window.location.toString()}` : '';
    logoutUser({ redirect: redirectParam });
    return null;
  }

  const invalidateUser = async () => {
    await queryClient.refetchQueries(usersKeys.user());
  };

  const invalidateNotificationCount = async () => {
    await queryClient.refetchQueries(notificationKeys.count());
  };

  const decodedToken: IDecodedToken | undefined = data
    ? decodeJwt(jwt)
    : undefined;

  // ROLES AND PERMISSIONS
  const isCompanyAdmin =
    `${data?.access}` === AccessRole.ADMIN ||
    `${data?.access}` === AccessRole.SUPERVISOR;

  const hasAccessToLocation = !!(
    locations &&
    locations.length &&
    locations.find(
      (location: ILocation) =>
        location.locationId.toString() === data?.customer.locationId.toString()
    )
  );

  const isResellerAdmin = isCompanyAdmin && !!data?.resellerAdmin;
  const superAdminRole = getSuperAdminRole(data);
  const isMultiLocation = hasLocationsEnabled && hasAccessToLocation;
  const hasLiveVideoAccess = !!data?.customer.liveAccess;

  const hasLeadsReports =
    data?.vinSolutionsDealer?.vinsolutionsConnect?.toString() === '1';

  const hasRegions = !!(data?.customer && isRegionsReseller(data));

  const organizationIds = userOrganizationAccess.map(
    userOrganizationAccess => userOrganizationAccess.organizationId
  );
  const isOrganizationAdmin = !!organizationIds.length;

  const userRoles: IUserRoles = {
    isCompanyAdmin,
    isMultiLocation,
    superAdminRole,
    hasRegions,
    isResellerAdmin,
    isOrganizationAdmin,
    hasLiveVideoAccess,
    hasLeadsReports,
    ...getAutomotiveFlags(data),
  };
  const preventUserEditAccess =
    data?.customer.preventUserEditAccess.toString() === '1' &&
    `${data?.access}` === AccessRole.USER;

  // Consider remove this method to string in future
  const payload = {
    ...toString({
      ...data,
      customer: { ...(data?.customer || {}) },
      reseller: { ...(data?.reseller || {}) },
      userOrganizationAccess: [...userOrganizationAccess],
    }),
  };

  const { trialExpired, trialDays } = calculateTrialDaysLeft({
    trialAccount: payload.trialAccount,
    id: payload.id,
    decodedToken,
  });

  const credit700Url = data?.customer?.credit700Url;

  const setJwtAndUpdateToken = (
    tokens: {
      accessToken: string;
      refreshToken?: string;
      adminJwt?: string;
    },
    shouldStoreTokens = true
  ) => {
    const whiteLabelRootDomain = whitelabel?.rootDomain || '';
    if (shouldStoreTokens) {
      updateAxiosInstaceToken(
        tokens.accessToken,
        tokens.refreshToken,
        tokens.adminJwt,
        whiteLabelRootDomain
      );
    }
    setJwt(tokens.accessToken);
  };

  const value = {
    userData: {
      ...userRoles,
      ...decodedToken,
      ...payload,
      preventUserEditAccess,
      trialDays,
      trialExpired,
      credit700Url,
    },
    setJwtAndUpdateToken,
    whitelabel: whitelabel,
    invalidateUser,
    invalidateNotificationCount,
    themeName,
    locations: locations || [],
  };

  Sentry.setUser({
    username: value?.userData?.username,
    id: value?.userData?.id,
  });

  return <AuthContext.Provider value={value} {...props} />;
}

const useAuth = () => React.useContext(AuthContext);

export { AuthProvider, useAuth };
